import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from './ui/Button';

export interface MnemonicGridProps {
  words: string[];
  /** 是否允许一键复制（默认开启；助记词场景建议传 false 并要求手抄）。 */
  copyable?: boolean;
  /** 遮盖模式：默认不遮盖（备份页必须展示）。 */
  masked?: boolean;
  onRevealToggle?: () => void;
  className?: string;
}

/**
 * 12 词助记词展示。
 * 安全约定：**不做持久化复制**（不写入剪贴板历史以外的任何存储），
 * 默认 `copyable = false`，强制用户手抄，避免助记词散落到剪贴板同步服务。
 */
export function MnemonicGrid({
  words,
  copyable = false,
  masked = false,
  onRevealToggle,
  className,
}: MnemonicGridProps): JSX.Element {
  const [revealed, setRevealed] = useState(!masked);

  const cells = useMemo(() => words.map((w, i) => ({ index: i + 1, word: w })), [words]);

  const toggle = (): void => {
    setRevealed((v) => !v);
    onRevealToggle?.();
  };

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="grid grid-cols-3 gap-2">
        {cells.map(({ index, word }) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-btn border border-line bg-panel-2 px-2 py-2"
          >
            <span className="w-5 shrink-0 text-right text-xs text-dim">{index}</span>
            <span className="mono text-sm text-text">{revealed ? word : '•••••'}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {masked ? (
          <Button variant="ghost" size="sm" onClick={toggle}>
            {revealed ? '遮盖' : '显示'}
          </Button>
        ) : null}
        {copyable ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void navigator.clipboard?.writeText(words.join(' '));
            }}
          >
            临时复制（不落盘）
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default MnemonicGrid;
